import { ApiResult, FieldType, ViewModelBase, isEmptyOrWhitespace, observable } from "@shoothill/core";
import { runInAction, action, computed } from "mobx";
import moment from "moment";

import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import {
    HouseKeepingAndRelatedResponseDTO,
    HouseKeepingFormModel,
    HouseKeepingProjectDetailsDTO,
    HouseKeepingRelatedResponseDTO,
    UpsertHouseKeepingAndRelatedRequestDTO,
    HouseKeepingDTO,
    IssuedByUsersDTO,
    SupplierDTO,
    HouseKeepingPhotoFile,
    HouseKeepingPhotosDTO,
} from "./HouseKeepingFormModel";
import { NonConformanceListViewModel } from "../NonConformanceListViewModel";

export class HouseKeepingFormViewModel extends ViewModelBase<HouseKeepingFormModel> {
    public server: ServerViewModel = new ServerViewModel();

    constructor(id: string | null, projectId: string | null, nonConformanceTypeId: string | null) {
        super(new HouseKeepingFormModel());
        this.setDecorators(HouseKeepingFormModel);

        this.model.id = id;
        this.model.projectId = projectId!;
        this.model.nonConformanceTypeId = nonConformanceTypeId!;

        isEmptyOrWhitespace(this.model.id) ? this.loadRelated() : this.loadWithRelated();
    }

    // #region Shared

    @observable
    public housekeepingProjectDetails: HouseKeepingProjectDetailsDTO | null = null;

    @computed
    public get projectDisplayName() {
        return `(${!isEmptyOrWhitespace(this.housekeepingProjectDetails?.projectReference) ? this.housekeepingProjectDetails?.projectReference : "--"} - 
                 ${!isEmptyOrWhitespace(this.housekeepingProjectDetails?.projectName) ? this.housekeepingProjectDetails?.projectName : "--"})`;
    }

    public nonConformanceTypeDisplayName = (list: NonConformanceListViewModel): string => {
        const id = isEmptyOrWhitespace(this.model.id) ? this.model.nonConformanceTypeId : this.houseKeeping?.nonConformanceTypeId;

        return list.nonConformanceTypes.find((i: any) => i.id === id)?.displayName ?? "";
    };

    // #endregion Shared

    // #region Properties

    @action
    public setSupplier = (value: SupplierDTO | null): void => {
        this.setValue("supplierId", value?.id ?? HouseKeepingFormModel.DEFAULT_SUPPLIER_ID);
    };

    @computed
    public get supplier(): SupplierDTO | null {
        return this.model.suppliers.find((u) => u.id === this.model.supplierId) ?? null;
    }

    @computed
    public get suppliers() {
        return this.model.suppliers;
    }

    @action
    public setIssuedByUser = (value: IssuedByUsersDTO | null): void => {
        this.setValue("issuedByUserId", value?.id ?? HouseKeepingFormModel.DEFAULT_ISSUED_BY_USER_ID);
    };

    @computed
    public get issuedByUser(): IssuedByUsersDTO | null {
        return this.model.issuedByUsers.find((u) => u.id === this.model.issuedByUserId) ?? null;
    }

    @computed
    public get issuedByUsers() {
        return this.model.issuedByUsers;
    }

    @action
    public setSignature = (data: string) => {
        this.setValue("signatureIssuedByURL", data);
        this.setValue("signatureIssuedByDate", new Date().toISOString());
    };

    // #endregion Properties

    // #region ReadOnly Properties

    @observable
    public houseKeeping: any = null;

    @computed
    public get readOnlyDescriptionDisplayName(): string {
        return this.houseKeeping?.description ?? "";
    }

    @computed
    public get readOnlyFormDate(): string {
        if (this.houseKeeping?.formDate) {
            return moment(this.houseKeeping?.formDate).format("DD/MM/YYYY");
        }
        return "";
    }

    public get readOnlySupplierDisplayName(): string {
        return this.suppliers.find((i: any) => i.id === this.houseKeeping?.supplierId)?.displayName ?? "";
    }

    // #endregion ReadOnly Properties

    @observable
    public houseKeepingPhotos = observable<HouseKeepingPhotosDTO>([]);

    @observable
    public snackMessage = "";

    @observable
    public snackType = "";

    @observable
    public SNACKSUCCESS = "success";

    @observable
    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @computed
    public get hasId(): boolean {
        return this.model.id !== null && this.model.id !== undefined && this.model.id !== "";
    }

    @computed
    public get getTodayDateFormatted(): string {
        return this.model.createdDate ? moment(this.model.createdDate).format("DD/MM/YYYY").toString() : moment().format("DD/MM/YYYY").toString();
    }

    @computed
    public get isFormDisabled(): boolean {
        return this.model.id !== null && this.model.id !== undefined && this.model.id !== "";
    }

    // Start Validation

    @computed
    private get validateSupplierId() {
        const errorMessage = this.model.validateSupplierId;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }
    @computed
    private get validateIssuedByUserId() {
        const errorMessage = this.model.validateIssuedByUserId;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }
    @computed
    private get validateSignatureIssuedByURL() {
        const errorMessage = this.model.validateSignatureIssuedByURL;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    //End Validation

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
        this.housekeepingProjectDetails = null;
    };

    @action
    public handleCancel(projectId: string | null): void {
        this.reset();
        this.history.push(AppUrls.Client.Project.ConstructionQuality.replace(":projectid", projectId ? projectId : this.model.projectId) + "#nonconformance");
    }

    @action
    public setHouseKeepingPhotosFile = (file: HouseKeepingPhotoFile) => {
        this.model.housekeepingPhotos.push(file);
    };

    @action
    public deleteHouseKeepingPhotosFile = async (index: number): Promise<void> => {
        this.model.housekeepingPhotos[index].isDeleted = true;
    };

    public upsert = async (): Promise<void> => {
        // APM: Review Note.
        // Shouldn't need this. The server viewmodel has and automatically sets an IsBusy flag.
        this.setIsLoading(true);

        let houseKeepingPhotos: HouseKeepingPhotosDTO[] = [];

        if (!this.hasId) {
            this.model.housekeepingPhotos.forEach((houseKeepingPhoto) => {
                const photo: HouseKeepingPhotosDTO = {
                    housekeepingId: houseKeepingPhoto.housekeepingId,
                    fileName: houseKeepingPhoto.fileName,
                    photoUrl: houseKeepingPhoto.photoURL,
                    createdByUserId: null,
                    isDeleted: false,
                };
                houseKeepingPhotos.push(photo);
            });
        }

        // Create the request body dto.
        const model: HouseKeepingDTO = this.model.toDto();

        const request: UpsertHouseKeepingAndRelatedRequestDTO = {
            housekeeping: model,
            houseKeepingPhotos: houseKeepingPhotos,
        };

        return await this.server
            .command<HouseKeepingAndRelatedResponseDTO>(
                () => this.Post(AppUrls.Server.Projects.Construction.NonConformance.Housekeeping.Upsert, request),
                (result: HouseKeepingAndRelatedResponseDTO) => {
                    runInAction(() => {
                        if (result) {
                            this.handleCancel(result.housekeeping.projectId);
                        }
                    });
                },
                this.isMyModelValid,
                "There was an error trying to send the non-conformance",
            )
            .finally(() => this.setIsLoading(false));
    };

    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) === false) {
            isValid = false;
        }

        return isValid;
    };

    public loadRelated = async (): Promise<void> => {
        this.setIsLoading(true);

        return await this.server
            .query<HouseKeepingRelatedResponseDTO>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.NonConformance.Housekeeping.Related}\\${this.model.projectId}\\${this.model.nonConformanceTypeId}`),
                (result) => {
                    runInAction(() => {
                        this.model.suppliers.replace(result.suppliers);
                        this.model.issuedByUsers.replace(result.issuedByUsers);
                        this.housekeepingProjectDetails = result.housekeepingProjectDetails;
                    });
                },
            )
            .finally(() => this.setIsLoading(false));
    };

    public loadWithRelated = async (): Promise<void> => {
        this.setIsLoading(true);

        return await this.server
            .query<any>(
                () => this.Get(`${AppUrls.Server.Projects.Construction.NonConformance.Housekeeping.WithRelatedById}\\${this.model.id}`),
                (result) => {
                    runInAction(() => {
                        this.model.suppliers.replace(result.suppliers);
                        this.model.issuedByUsers.replace(result.issuedByUsers);
                        this.housekeepingProjectDetails = result.housekeepingProjectDetails;
                        this.houseKeeping = result.housekeeping;
                        result.housekeepingPhotos.map((el: any) => this.setHouseKeepingPhotosFile(el));
                    });
                },
            )
            .finally(() => this.setIsLoading(false));
    };

    public fileUpload = async (data: any): Promise<ApiResult<any>> => {
        const formData = new FormData();
        formData.append("formFile", data.formFile);
        formData.append("fileName", data.fileName);
        const apiResult = await this.Post<any>(AppUrls.Server.File.UploadFile, formData);
        if (apiResult) {
            if (!apiResult.wasSuccessful) {
                console.log(apiResult.errors);
                runInAction(() => {
                    this.setSnackMessage("Error uploading file please try again.");
                    this.setSnackType(this.SNACKERROR);
                    this.setSnackbarState(true);
                });
            }
        }
        return apiResult;
    };

    public fileChange = async (event: any): Promise<void> => {
        if (event.target.files.length > 0) {
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = null;
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                let fileToDisplay: HouseKeepingPhotoFile = {
                    housekeepingId: null,
                    fileName: data.fileName,
                    photoURL: apiResult.payload,
                    isDeleted: false,
                    createdByUserId: "",
                };
                this.setHouseKeepingPhotosFile(fileToDisplay);
            }
        }
    };

    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, fileUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<HouseKeepingFormModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (this.server.IsSubmitted) {
            switch (fieldName) {
                case "supplierId": {
                    const result = this.validateSupplierId;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "issuedByUserId": {
                    const result = this.validateIssuedByUserId;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
                case "signatureIssuedByURL": {
                    const result = this.validateSignatureIssuedByURL;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
            }
        } else {
            errorMessage = "";
            isValid = true;
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boilerplate
}
